import { DocumentImageOptimized } from "./utils";
import { useEffect, useState, useMemo, useCallback, memo } from "react";
import axios from "../../../api/axios";
import PdfPlaceholder from "../../../utils/pdf-placeholder.png";
import { PMDialog } from "../../utils/PMDialog";
import { useTranslation } from "react-i18next";
import {
  ArrowTopRightOnSquareIcon,
  ArrowPathIcon,
  DocumentIcon,
  EyeIcon,
  XMarkIcon
} from "@heroicons/react/24/outline";
import { useApiCache } from "../../../hooks/useApiCache";

const TinyDocumentSectionComponent = (props) => {
  const {
    module,
    moduleParam,
    title = "Dokumenter",
    showNoDocuments = true,
    forceRefresh = false,
    refreshButton = false,
    maxDocumentDisplay = 10,
    refreshTrigger = 0,
    documents: propsDocuments,
  } = props;
  const { t } = useTranslation();
  const { getCachedData, setCachedData } = useApiCache();

  const [state, setState] = useState({
    documents: Array.isArray(propsDocuments) ? propsDocuments : [],
    isLoading: false,
  });

  const [showAllDialog, setShowAllDialog] = useState(false);
  // Initialize documentsLoaded based on whether we have documents initially
  // If we have documents from props, mark as loaded immediately
  const [documentsLoaded, setDocumentsLoaded] = useState(() => {
    return !!(propsDocuments && propsDocuments.length > 0);
  });

  const { documents: documentsRaw, isLoading } = state;
  const documents = Array.isArray(documentsRaw) ? documentsRaw : [];

  const getDocuments = useCallback(async (skipCache = false) => {
    const cacheKey = `tiny_documents_${module}_${moduleParam}`;
    
    if (!skipCache) {
      const cachedData = getCachedData(cacheKey);
      if (cachedData && cachedData.documents && cachedData.documents.length > 0) {
        setState(cachedData);
        // Documents from cache are immediately available, no need to set loading state
        setDocumentsLoaded(true);
        return;
      }
    }

    // Only set loading state when we're actually fetching from API
    setState(prevState => ({ ...prevState, isLoading: true }));
    // Only hide documents when we're fetching new data
    setDocumentsLoaded(false);
    
    const params = {
      module: module,
      module_param: moduleParam,
    };
    
    try {
      const response = await axios.get(`/documents`, { params });
      const list = response.data?.documents;
      const newState = {
        documents: Array.isArray(list) ? list : [],
        isLoading: false,
      };
      setState(newState);
      setCachedData(cacheKey, newState);
      // Trigger fade-in animation after a short delay
      setTimeout(() => setDocumentsLoaded(true), 50);
    } catch (error) {
      console.error('Error fetching documents:', error);
      setState({
        documents: [],
        isLoading: false,
      });
      setDocumentsLoaded(true);
    }
  }, [module, moduleParam, getCachedData, setCachedData]);

  const handleManualRefresh = useCallback(() => {
    setDocumentsLoaded(false);
    getDocuments(true); // Skip cache
  }, [getDocuments]);

  // Memoize documents array length to prevent unnecessary re-renders
  const documentsArrayLength = useMemo(() => {
    return propsDocuments && Array.isArray(propsDocuments) ? propsDocuments.length : 0;
  }, [propsDocuments?.length]);

  // Create a stable reference for documents IDs comparison
  // This will recalculate when propsDocuments changes, but memo comparison will prevent re-renders
  // if the actual IDs haven't changed
  const documentsIds = useMemo(() => {
    if (!propsDocuments || !Array.isArray(propsDocuments) || propsDocuments.length === 0) return '';
    return propsDocuments.map(d => d?.id).filter(Boolean).sort().join(',');
  }, [propsDocuments]);

  useEffect(() => {
    if (module && moduleParam) {
      // If we have documents from props, use them directly without loading state
      if (documentsArrayLength > 0 && propsDocuments && Array.isArray(propsDocuments)) {
        setState(prevState => {
          // Only update if documents actually changed
          const currentIds = prevState.documents?.map(d => d?.id).sort().join(',') || '';
          const newIds = propsDocuments.map(d => d?.id).sort().join(',');
          if (currentIds !== newIds) {
            return {
              ...prevState,
              documents: Array.isArray(propsDocuments) ? propsDocuments : [],
            };
          }
          return prevState;
        });
        // Documents from props are immediately available
        setDocumentsLoaded(true);
      } else if (documentsArrayLength === 0) {
        // Only fetch if we don't have documents from props
        // Check if we need to refresh or if this is initial load
        const shouldRefresh = forceRefresh || refreshTrigger > 0;
        getDocuments(shouldRefresh);
      }
    }
    // Only depend on stable values, not the array reference itself
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [module, moduleParam, forceRefresh, refreshTrigger, documentsIds, documentsArrayLength, getDocuments]);

  function isPdf(filename) {
    return filename?.toLowerCase().endsWith(".pdf");
  }

  const navigateToPdf = (url) => {
    window.open(url, "_blank");
  };

  const handleDocumentClick = useCallback((document) => {
    if (isPdf(document?.file?.filename)) {
      navigateToPdf(document?.file?.url);
    } else {
      // For images, also open in new window
      window.open(document?.file?.url, "_blank");
    }
  }, []);

  const DialogContent = useCallback(() => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 bg-blue-100 rounded-lg flex items-center justify-center">
            <DocumentIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title || "Dokumenter"}</h3>
            <p className="text-sm text-gray-500">{documents.length} dokumenter</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isLoading}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Oppdater
          </button>
          <button
            type="button"
            onClick={() => setShowAllDialog(false)}
            className="inline-flex items-center justify-center p-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            title={t("Lukk")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {documents.map((document, index) => {
          const isPdfDocument = isPdf(document?.file?.filename);

          return (
            <div 
              key={document.id} 
              className="group bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all duration-200 overflow-hidden"
              style={{ 
                opacity: documentsLoaded ? 1 : 0,
                transition: `opacity 0.4s ease-in-out ${index * 50}ms`
              }}
            >
              {/* Document Preview */}
              <div className="aspect-square bg-gray-50 relative overflow-hidden">
                <DocumentImageOptimized
                  pathType="quickview"
                  document={document}
                  open={false}
                  fallback={
                    document?.file?.type === "application/pdf"
                      ? PdfPlaceholder
                      : null
                  }
                />
                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={() => handleDocumentClick(document)}
                    className="opacity-0 group-hover:opacity-100 bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-3 transition-all duration-200 transform scale-75 group-hover:scale-100"
                    title={t("Åpne i nytt vindu")}
                  >
                    <EyeIcon className="h-5 w-5 text-gray-700" />
                  </button>
                </div>
              </div>

              {/* Document Info */}
              <div className="p-4">
                <h4 className="text-sm font-medium text-gray-900 truncate mb-1">
                  {document?.title || "Uten tittel"}
                </h4>
                <p className="text-xs text-gray-500 truncate mb-3">
                  {document?.file?.filename || "Ukjent fil"}
                </p>
                
                {/* Action Button */}
                <button
                  type="button"
                  onClick={() => handleDocumentClick(document)}
                  className="w-full inline-flex items-center justify-center px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
                  Åpne
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  ), [documents, isLoading, title, handleDocumentClick, t, documentsLoaded, handleManualRefresh, setShowAllDialog]);

  // Calculate which documents to show and how many are hidden
  const visibleDocuments = documents.slice(0, maxDocumentDisplay);
  const hiddenCount = Math.max(0, documents.length - maxDocumentDisplay);
  const shouldShowAllButton = documents.length > maxDocumentDisplay;

  return (
    <div className="w-full">
      {documents?.length > 0 ? (
        <>
          {refreshButton && (
            <div className="flex items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">{title}</h3>
              <button
                type="button"
                onClick={handleManualRefresh}
                disabled={isLoading}
                className="ml-auto inline-flex items-center p-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title={t("Refresh documents")}
              >
                <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          )}
          <div className="flex flex-wrap gap-4 py-2">
            {visibleDocuments.map((document, index) => {
              const isPdfDocument = isPdf(document?.file?.filename);

              return (
                <div
                  key={document.id}
                  className="flex flex-col items-center w-24"
                  style={{ 
                    opacity: documentsLoaded ? 1 : 0,
                    transition: `opacity 0.4s ease-in-out ${index * 50}ms`
                  }}
                >
                  <div
                    className="h-20 w-20 flex-shrink-0 cursor-pointer"
                    onClick={() => handleDocumentClick(document)}
                  >
                    <DocumentImageOptimized
                      pathType="quickview"
                      document={document}
                      open={false}
                      fallback={
                        document?.file?.type === "application/pdf"
                          ? PdfPlaceholder
                          : null
                      }
                    />
                  </div>
                  <div className="mt-1 text-center">
                    <p className="text-xs font-medium text-gray-900 truncate w-20" title={document?.title}>
                      {document?.title || "Uten tittel"}
                    </p>
                    <p className="text-xs text-gray-500 truncate w-20" title={document?.file?.filename}>
                      {document?.file?.filename || "Ukjent fil"}
                    </p>
                  </div>
                </div>
              );
            })}
            {/* Show all button - only show if there are hidden documents */}
            {shouldShowAllButton && (
              <div 
                className="flex flex-col items-center w-24"
                style={{ 
                  opacity: documentsLoaded ? 1 : 0,
                  transition: `opacity 0.4s ease-in-out ${visibleDocuments.length * 50}ms`
                }}
              >
                <button
                  type="button"
                  onClick={() => setShowAllDialog(true)}
                  className="h-20 w-20 flex-shrink-0 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center hover:border-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-400">+</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {t("Show all")}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {hiddenCount} {hiddenCount === 1 ? 'more' : 'more'}
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        showNoDocuments && (
          <span className="italic text-sm text-gray-500">
            Ingen dokumenter eller filer tilgjengelig
          </span>
        )
      )}

      {/* Show All Dialog */}
      <PMDialog
        open={showAllDialog}
        onClose={() => setShowAllDialog(false)}
        title={title || "Dokumenter"}
        size="7xl"
      >
        <DialogContent />
      </PMDialog>
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const TinyDocumentSection = memo(TinyDocumentSectionComponent, (prevProps, nextProps) => {
  // Custom comparison function to prevent re-renders when props haven't meaningfully changed
  return (
    prevProps.module === nextProps.module &&
    prevProps.moduleParam === nextProps.moduleParam &&
    prevProps.title === nextProps.title &&
    prevProps.showNoDocuments === nextProps.showNoDocuments &&
    prevProps.forceRefresh === nextProps.forceRefresh &&
    prevProps.refreshButton === nextProps.refreshButton &&
    prevProps.maxDocumentDisplay === nextProps.maxDocumentDisplay &&
    prevProps.refreshTrigger === nextProps.refreshTrigger &&
    // Compare documents arrays by length and IDs
    (prevProps.documents?.length || 0) === (nextProps.documents?.length || 0) &&
    (prevProps.documents?.map(d => d?.id).join(',') || '') === (nextProps.documents?.map(d => d?.id).join(',') || '')
  );
});
